import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button-link'
import { NAV_LINKS } from '@/constants/content'
import { siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        const onResize = (): void => {
            if (window.innerWidth >= 768) setOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            document.body.style.overflow = ''
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const close = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300',
                open && 'border-line bg-white',
                !open && scrolled && 'border-line bg-white/85 backdrop-blur-md',
                !open && !scrolled && 'border-transparent bg-transparent'
            )}
        >
            <Container className='flex h-16 items-center justify-between gap-6 md:h-[72px]'>
                <a
                    href='/#top'
                    onClick={close}
                    className='rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-azure-500'
                >
                    <Logo />
                </a>

                <nav aria-label="Main" className='hidden md:block'>
                    <ul className='flex items-center gap-9'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='group relative py-2 font-mono text-[12px] uppercase tracking-[0.12em] text-ink/65 transition-colors hover:text-ink focus-visible:text-ink focus-visible:outline-none'
                                >
                                    {link.label}
                                    <span
                                        aria-hidden='true'
                                        className='absolute inset-x-0 -bottom-0.5 h-px origin-left scale-x-0 bg-azure-500 transition-transform duration-300 group-hover:scale-x-100 group-focus-visible:scale-x-100'
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-3'>
                    <ButtonLink href='/contact' className='hidden h-10 px-5 text-sm md:inline-flex'>
                        Start a project
                        <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </ButtonLink>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen(value => !value)}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-ink/15 bg-white/80 text-ink backdrop-blur-sm transition-colors hover:border-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azure-500 md:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        <span className='sr-only'>{open ? 'Close menu' : 'Open menu'}</span>
                    </button>
                </div>
            </Container>

            <div
                id='mobile-menu'
                hidden={!open}
                className='fixed inset-x-0 top-16 bottom-0 overflow-y-auto border-t border-line bg-white md:hidden'
            >
                <Container className='flex min-h-full flex-col justify-between gap-12 py-10'>
                    <nav aria-label="Mobile">
                        <ul className='flex flex-col'>
                            {NAV_LINKS.map((link, index) => (
                                <li key={link.href} className='border-b border-line'>
                                    <a
                                        href={link.href}
                                        onClick={close}
                                        className='flex items-baseline justify-between py-5 text-4xl font-medium tracking-[-0.04em] text-ink'
                                    >
                                        {link.label}
                                        <span className='font-mono text-[11px] tracking-[0.14em] text-muted-foreground'>
                                            0{index + 1}
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex flex-col gap-6'>
                        <ButtonLink href='/contact' onClick={close} className='w-full'>
                            Start a project
                            <ArrowUpRight className='size-4' />
                        </ButtonLink>

                        <a href={`mailto:${siteConfig.contact.email}`} className='font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground'>
                            {siteConfig.contact.email}
                        </a>
                    </div>
                </Container>
            </div>
        </header>
    )
}
